import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import Loading from "./Loading";
// Reuse the inquiry management styling so the two screens look identical
import "./AdminInquiries.css";
import Swal from "sweetalert2";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const MySwal = Swal.mixin({
    customClass: {
        container: "my-swal-container",
    },
});

const STATUSES = ["New", "Read", "Replied", "Closed"];
const PAGE_SIZE = 20;

function AdminContactEnquiries() {
    const { authFetch } = useContext(AuthContext);
    const navigate = useNavigate();

    const [enquiries, setEnquiries] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const [statusFilter, setStatusFilter] = useState("All");
    const [searchInput, setSearchInput] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [page, setPage] = useState(1);

    const [expandedId, setExpandedId] = useState(null);

    const formatDateTime = (value) => {
        if (!value) return "N/A";
        const date = new Date(value);
        if (isNaN(date)) return "N/A";
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${day}/${month}/${date.getFullYear()} ${hours}:${minutes}`;
    };

    const fetchEnquiries = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(page),
                pageSize: String(PAGE_SIZE),
            });
            if (statusFilter !== "All") params.append("status", statusFilter);
            if (appliedSearch) params.append("search", appliedSearch);

            const response = await authFetch(`${API_BASE_URL}/api/ContactEnquiries?${params}`);
            if (!response.ok) throw new Error(`Failed to load enquiries (${response.status})`);

            const data = await response.json();
            setEnquiries(data.items || []);
            setTotalCount(data.totalCount || 0);
        } catch (err) {
            MySwal.fire({
                title: "Error!",
                text: err.message || "Failed to load enquiries.",
                icon: "error",
                confirmButtonColor: "#45a049",
            });
        } finally {
            setLoading(false);
        }
    }, [authFetch, page, statusFilter, appliedSearch]);

    useEffect(() => {
        fetchEnquiries();
    }, [fetchEnquiries]);

    const applyFilters = () => {
        setAppliedSearch(searchInput.trim());
        setPage(1);
    };

    const changeStatus = async (enquiry, newStatus) => {
        try {
            const response = await authFetch(
                `${API_BASE_URL}/api/ContactEnquiries/${enquiry.id}/status`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ status: newStatus }),
                }
            );

            if (!response.ok) {
                const err = await response.json().catch(() => null);
                throw new Error(err?.message || `Failed to update status (${response.status})`);
            }

            fetchEnquiries();
        } catch (err) {
            MySwal.fire({
                title: "Error!",
                text: err.message,
                icon: "error",
                confirmButtonColor: "#45a049",
            });
        }
    };

    const handleCsvExport = () => {
        try {
            const escapeCSV = (value) => {
                if (value === null || value === undefined) return "";
                const str = String(value);
                if (str.includes(",") || str.includes('"') || str.includes("\n")) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            };

            // Header column count must match rowData below exactly
            let csvContent = "﻿";
            csvContent += "No.,Submitted,Name,Email,Phone,Status,Message\n";

            enquiries.forEach((item, index) => {
                const rowData = [
                    index + 1,
                    escapeCSV(formatDateTime(item.submittedDate)),
                    escapeCSV(item.fullName),
                    escapeCSV(item.email),
                    escapeCSV(item.phoneNumber),
                    escapeCSV(item.status),
                    escapeCSV(item.message),
                ];
                csvContent += rowData.join(",") + "\n";
            });

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const stamp = new Date();
            const stampStr = `${stamp.getFullYear()}-${String(stamp.getMonth() + 1).padStart(2, "0")}-${String(stamp.getDate()).padStart(2, "0")}`;
            link.download = `Ardency_Contact_Enquiries_${stampStr}.csv`;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: "Failed to export CSV. Please try again.",
                icon: "error",
                confirmButtonColor: "#45a049",
            });
        }
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    if (loading) {
        return (
            <div className="admin-inquiry-container">
                <div className="admin-inquiry-loading">
                    <Loading />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-inquiry-container">
            <div className="admin-inquiry-header">
                <button
                    className="admin-inquiry-back-btn"
                    onClick={() => navigate("/admin/dashboard")}
                >
                    <i className="fas fa-arrow-left"></i> Back to Dashboard
                </button>
            </div>

            <div className="admin-inquiry-title">
                <h1>Contact Enquiries</h1>
            </div>

            {/* Filters */}
            <div className="admin-inquiry-filter">
                <div className="admin-inquiry-filter-group">
                    <label>Search</label>
                    <input
                        type="text"
                        placeholder="Search by name, email, phone or message..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") applyFilters();
                        }}
                    />
                </div>

                <div className="admin-inquiry-filter-group">
                    <label>Status</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="All">All Statuses</option>
                        {STATUSES.map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </div>

                <button className="admin-inquiry-filter-btn" onClick={applyFilters}>
                    Search
                </button>
                <button className="admin-inquiry-filter-btn" onClick={handleCsvExport}>
                    Export Excel
                </button>
            </div>

            <div className="admin-inquiry-table-container">
                {enquiries.length > 0 ? (
                    <table className="admin-inquiry-table">
                        <thead>
                            <tr>
                                <th style={{ width: "150px" }}>Submitted</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th style={{ width: "130px" }}>Phone</th>
                                <th>Message</th>
                                <th style={{ width: "130px" }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {enquiries.map((item) => {
                                const isOpen = expandedId === item.id;
                                const preview =
                                    item.message && item.message.length > 80
                                        ? `${item.message.slice(0, 80)}...`
                                        : item.message;

                                return (
                                    <tr key={item.id}>
                                        <td>{formatDateTime(item.submittedDate)}</td>
                                        <td>{item.fullName}</td>
                                        <td>
                                            <a href={`mailto:${item.email}`}>{item.email}</a>
                                        </td>
                                        <td>{item.phoneNumber || "N/A"}</td>
                                        <td style={{ whiteSpace: "pre-wrap", maxWidth: "420px" }}>
                                            {isOpen ? item.message : preview}
                                            {item.message && item.message.length > 80 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedId(isOpen ? null : item.id)}
                                                    style={{
                                                        display: "block",
                                                        marginTop: "4px",
                                                        background: "none",
                                                        border: "none",
                                                        color: "#0d7ea8",
                                                        cursor: "pointer",
                                                        padding: 0,
                                                        fontSize: "12px",
                                                    }}
                                                >
                                                    {isOpen ? "Show less" : "Show more"}
                                                </button>
                                            )}
                                        </td>
                                        <td>
                                            <select
                                                value={item.status}
                                                onChange={(e) => changeStatus(item, e.target.value)}
                                            >
                                                {STATUSES.map((s) => (
                                                    <option key={s} value={s}>
                                                        {s}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <div className="admin-inquiry-empty">
                        <i className="fas fa-envelope-open-text"></i>
                        <p className="primary-message">No enquiries found.</p>
                        <p className="secondary-message">
                            Enquiries submitted through the Contact Us page will appear here.
                        </p>
                    </div>
                )}
            </div>

            {totalCount > PAGE_SIZE && (
                <div className="admin-inquiry-pagination">
                    <button
                        className="admin-inquiry-pagination-btn"
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                    >
                        <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button
                        className="admin-inquiry-pagination-btn"
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                    >
                        <i className="fas fa-angle-left"></i>
                    </button>
                    <span style={{ padding: "0 12px" }}>
                        Page {page} of {totalPages}
                    </span>
                    <button
                        className="admin-inquiry-pagination-btn"
                        onClick={() => setPage(page + 1)}
                        disabled={page >= totalPages}
                    >
                        <i className="fas fa-angle-right"></i>
                    </button>
                    <button
                        className="admin-inquiry-pagination-btn"
                        onClick={() => setPage(totalPages)}
                        disabled={page >= totalPages}
                    >
                        <i className="fas fa-angle-double-right"></i>
                    </button>
                </div>
            )}
        </div>
    );
}

export default AdminContactEnquiries;
